import {
  CalendarMonthOutlined,
  CommentOutlined,
  FormatListBulletedRounded,
  HowToVoteOutlined,
  ThumbUp,
  ThumbUpOutlined,
  VisibilityOffRounded,
} from "@mui/icons-material";
import { Button, Divider, Link, Stack, SxProps, Tooltip, Typography, useTheme } from "@mui/material";
import { DateTime } from "luxon";
import { Avatar, AvatarGroup, MaterialSymbolIcon, MaterialSymbols } from "..";
import { IssuePrivacyType, Links } from "../../constants";
import { useSession } from "../../hooks";
import { IssueSummaryDto } from "../../http";

interface IProps {
  issue: IssueSummaryDto;
  onUpvoteClicked: () => void;
  sx?: SxProps;
}

const IssueIcons = (props: IProps) => {
  const { userId } = useSession();
  const { issue } = props;
  const collaborators = issue.collaborators ?? [];
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      spacing={0.25}
      divider={<Divider orientation="vertical" sx={{ height: "12px", borderColor: theme.palette.line.main }} />}
      sx={{
        alignItems: "center",
        ...props.sx,
      }}
    >
      <Avatar
        user={issue.createdByUser}
        tooltipContent={(user) => (
          <>
            Creator: <Link href={Links.User(user.userId)}>{user.displayName}</Link>
          </>
        )}
        size="xsmall"
        sx={{ width: 20, height: 20, fontSize: 12 }}
      />

      {issue.decisionMaker && (
        <Avatar
          user={issue.decisionMaker}
          tooltipContent={(user) => (
            <>
              Decision Maker: <Link href={Links.User(user.userId)}>{user.displayName}</Link>
            </>
          )}
          size="xsmall"
          sx={{ width: 20, height: 20, fontSize: 12 }}
        />
      )}

      {collaborators.length > 0 && (
        <AvatarGroup
          max={2}
          sx={{
            "& .MuiAvatar-root": { width: 20, height: 20, fontSize: 12 },
          }}
          users={collaborators}
          avatarComponent={(user) => <Avatar user={user} tooltip />}
        />
      )}

      <Tooltip
        placement="bottom-end"
        title={
          issue.upvotes.length > 0
            ? issue.upvotes
                .sort((a, b) => a.displayName.localeCompare(b.displayName))
                .map((u, i) => [
                  <Typography key={i} sx={{ display: "inline-block", fontSize: 13, marginRight: "2px" }}>
                    <Link href={Links.User(u.userId)}>{u.displayName}</Link>
                    {i < issue.upvotes.length - 1 && ", "}
                  </Typography>,
                ])
            : null
        }
      >
        <Stack direction="row" spacing={0.1} sx={{ alignItems: "center" }}>
          <Button
            size="small"
            disableRipple
            onClick={(e) => {
              props.onUpvoteClicked();
              e.stopPropagation();
            }}
            sx={{ minWidth: 0, textDecoration: "none !important" }}
            startIcon={
              issue.upvotes.findIndex((x) => x.userId === userId) !== -1 ? (
                <ThumbUp sx={{ color: "text.secondary" }} />
              ) : (
                <ThumbUpOutlined sx={{ color: "text.secondary" }} />
              )
            }
          >
            <Typography variant="caption">{issue.upvotes.length}</Typography>
          </Button>
        </Stack>
      </Tooltip>

      <Tooltip title="Created On">
        <Stack direction="row" spacing={0.1} sx={{ alignItems: "center" }}>
          <CalendarMonthOutlined sx={{ color: "text.secondary" }} />
          <Typography variant="caption">
            {DateTime.fromISO(props.issue.createdDateTimeUtc, { zone: "utc" })
              .toLocal()
              .toLocaleString(DateTime.DATE_MED)}
          </Typography>
        </Stack>
      </Tooltip>

      {props.issue.hasImpact && (
        <Tooltip title="Impact">
          <MaterialSymbolIcon symbol={MaterialSymbols.Bolt} sx={{ color: "text.secondary" }} />
        </Tooltip>
      )}

      {props.issue.proposalCount > 0 && (
        <Tooltip title="Proposals">
          <Stack direction="row" spacing={0.1} sx={{ alignItems: "center" }}>
            <HowToVoteOutlined sx={{ color: "text.secondary" }} />
            <Typography variant="caption">{props.issue.proposalCount}</Typography>
          </Stack>
        </Tooltip>
      )}

      {props.issue.hasDecision && (
        <Tooltip title="Decision">
          <MaterialSymbolIcon symbol={MaterialSymbols.Gavel} sx={{ color: "text.secondary" }} />
        </Tooltip>
      )}

      {props.issue.taskCount > 0 && (
        <Tooltip title="Tasks">
          <Stack direction="row" spacing={0.1} sx={{ alignItems: "center" }}>
            <FormatListBulletedRounded sx={{ color: "text.secondary" }} />
            <Typography variant="caption">{props.issue.taskCount}</Typography>
          </Stack>
        </Tooltip>
      )}

      {props.issue.privacy === IssuePrivacyType.Limited && (
        <Tooltip
          title={
            <Stack>
              <Typography>Limited</Typography>
              <Typography variant="caption" sx={{ lineHeight: "1rem" }}>
                Visible to creator, decision maker, collaborators, and assigned team members.
              </Typography>
            </Stack>
          }
          slotProps={{
            tooltip: {
              sx: {
                color: theme.palette.error.main,
              },
            },
          }}
        >
          <VisibilityOffRounded sx={{ color: theme.palette.error.main }} />
        </Tooltip>
      )}

      {props.issue.commentCount > 0 && (
        <Tooltip title="Comments">
          <Stack direction="row" spacing={0.1} sx={{ alignItems: "center" }}>
            <CommentOutlined sx={{ color: "text.secondary" }} />
            <Typography variant="caption">{props.issue.commentCount}</Typography>
          </Stack>
        </Tooltip>
      )}
    </Stack>
  );
};

export { IssueIcons };
